import { ChangeDetectorRef, Component, inject, OnDestroy, OnInit, viewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
    AixButtonComponent,
    AixDataTestingDirective,
    AixDropdownComponent,
    AixHeaderSectionComponent,
    AixInputComponent,
    AixLoadingComponent,
    AixModalComponent,
    AixNotificationComponent,
    BUTTON_TYPE
} from '@trade-platform/ui-components';
import { Subscription } from 'rxjs';
import { BaseOrdersStoreFacade, ORDERS_STORE_FACADE } from '../../base.orders.store.facade';

import {
    canAccessOrderAdmin,
    isAixOrderAdmin,
    isClientOrderAdminOnly,
    ProfileStoreFacade
} from '@advisor-ui/app-services';
import {
    ReactiveFormsModule,
    UntypedFormControl,
    UntypedFormGroup,
    Validators
} from '@angular/forms';
import { envelopeStatus, orderStatus } from '@trade-platform/lib-enums';
import {
    eSignEnvelopeRecipientStatusType,
    OrderApprovalConfig,
    OrderQueueOptions,
    OrderRecipientChangeAction
} from '@trade-platform/ui-shared';
import { getOrderStatusIndex } from '../../utils';

@Component({
    selector: 'aix-order-details-admin',
    templateUrl: './order-admin.html',
    styleUrls: ['./order-admin.scss'],
    standalone: true,
    imports: [
        AixButtonComponent,
        AixDropdownComponent,
        AixHeaderSectionComponent,
        AixInputComponent,
        AixModalComponent,
        AixLoadingComponent,
        AixNotificationComponent,
        ReactiveFormsModule,
        AixDataTestingDirective
    ]
})
export class AixOrdersDetailAdminComponent implements OnInit, OnDestroy {
    route = inject(ActivatedRoute);
    router = inject(Router);
    store = inject<BaseOrdersStoreFacade>(ORDERS_STORE_FACADE);
    ref = inject(ChangeDetectorRef);
    profileStoreFacade = inject(ProfileStoreFacade);

    readonly reducerSuffix = this.store.type;

    /**
     * Narrows the panel to the two actions roadmap#333 delegates to client tenants — Replace Form
     * PDF and Mark Order Complete. Everything else stays AIX-only, and admin-api refuses it for
     * these callers regardless, so hiding the cards keeps the UI from offering an action that
     * cannot succeed.
     *
     * Convert Order to Wetsign was delegated too for a day and product pulled it back after testing
     * in UAT — "the only change we should make is not make the Convert to Wetsign available to
     * System Admins" (roadmap#333, comment 5542931994). Its card is back inside the AIX-only block,
     * and admin-api refuses these callers again, so do not move it out without that decision being
     * revisited.
     *
     * A getter, not a field: isFeatureEnabled reads the profile out of localStorage, so a field
     * initializer would freeze the answer at construction time.
     */
    get isClientOrderAdminOnly() {
        return isClientOrderAdminOnly(this.profileStoreFacade.profile);
    }

    /**
     * Gates the whole panel in the template. Set by the ngOnInit guard below, whose redirect is
     * asynchronous — without this the panel paints once, every card, for an identity that is on its
     * way out. Buttons only, and no order data the caller cannot already read on the detail page,
     * but a refused caller should not see the tools at all.
     *
     * A plain field rather than a getter: it is the guard's own answer, recorded where the template
     * can read it, so the two cannot disagree.
     */
    canAccessAdmin = false;

    /**
     * Guards the one call this panel makes on load. getGatewayFiles requires the orderAdmin
     * FEATURE, so anyone else gets a 403 — and a 403 is not an error message here:
     * UnauthorizedInterceptor ends the session on any of them, before the action's own failure
     * handler runs. Dispatched unconditionally, it logged a client system administrator out every
     * time they opened the tab (roadmap#333).
     *
     * Phrased as the entitlement rather than as !isClientOrderAdminOnly, which is also false for a
     * caller being redirected away — that one is refused, not an AIX order admin, and would have
     * been logged out instead of redirected.
     */
    get canReadGatewayFiles() {
        return isAixOrderAdmin(this.profileStoreFacade.profile);
    }

    notificationRef = viewChild<AixNotificationComponent>(AixNotificationComponent);
    confirmationModal = viewChild<AixModalComponent>(AixModalComponent);

    notificationMessage = '';
    notificationType: 'ok' | 'error' = 'ok';

    // `detail` is optional: most actions need one sentence, but the replace confirmation has a
    // question and a consequence, which read badly run together in one paragraph.
    modalText: { title: string; text: string; detail?: string } = {
        title: '',
        text: ''
    };
    pendingAction: string | null = null;

    orderId: string;
    status: string;
    isWetsign: boolean;
    canConvertToWetsign: boolean;
    canReplaceForm: boolean;
    // Separate from canReplaceForm so the card can say WHICH gate is closed. An open envelope and a
    // pre-step-5 status are both refusals, but they need different wording and different advice.
    replaceFormEnvelopeOpen = false;
    canMoveBackInWorkflowStatus: boolean;
    hasPreviousApproval = false;
    canMoveForwardInWorkflow: boolean;
    hasValidAddBlindCarbonCopyToEnvelopeState: boolean;
    hasEnvelopeId: boolean;
    canPushProductProfileData: boolean;
    isIfgAdvisorOrder: boolean;
    canResubmitFileToIfgGateway: boolean;
    integrationApprovalConfig: OrderApprovalConfig | null = null;

    subscriptions: Subscription[] = [];

    orderForms: any[] = [];
    updateFormOpen = false;
    updateFormSelection = new UntypedFormGroup({
        formId: new UntypedFormControl('', Validators.required)
    });
    selectedFormId: string | null = null;

    // Form PDF replacement (roadmap#319). A replacement always corrects bad data rather than
    // standing in for the template, so the form stays system-owned and a later finalize will
    // regenerate it as usual.
    replaceFormOpen = false;
    // Deliberately separate from selectedFormId, which Push Updated Form owns. Sharing it meant a
    // form chosen in that card enabled this one's submit with nothing selected here.
    replaceFormId: string | null = null;
    replaceFormFile: File | null = null;
    // The upload is the one admin action that can take a while. While it is in flight the panel
    // locks: no re-submit, and no other admin action against an order that is mid-change.
    //
    // Deliberately component-local rather than read from adminReplaceFormOnOrder in the store. The
    // store slice is registered with StoreModule.forFeature and outlives this component, so a stale
    // inProgress would raise the full-screen scrim the moment the tab is opened, with no request
    // running to ever clear it — leave mid-upload via browser back, return, and the tab is stuck
    // until a reload. This flag starts clean on every mount instead.
    //
    // The effect's switchMap cannot strand it: a cancellation requires a second ORDER_REPLACE_FORM,
    // there is exactly one dispatch site, and it refuses while this flag is set — and a second
    // request would emit success or failure, clearing it anyway.
    replaceFormInFlight = false;
    replaceFormSelection = new UntypedFormGroup({
        formId: new UntypedFormControl('', Validators.required)
    });

    ifgGatewayFiles: any[] = [];
    resubmitFileOpen = false;
    resubmitFileSelection = new UntypedFormGroup({
        fileTransmissionId: new UntypedFormControl('', Validators.required)
    });
    selectedFileTransmissionId: string | null = null;

    /**
     * Move Order Queue (trade-platform#10469). Which signer queue - an OSJ, in IFG's vocabulary - an
     * order sits in is derived from the branch rules on the firm's signer approval configs, so the
     * options come from the API rather than from anything on the order.
     *
     * Deliberately not gated on the firm: a firm whose signer configs carry no branch rules gets an
     * empty list and the card does not render, which today means IFG only without naming IFG
     * anywhere. `queueOptions` excludes the queue the order is already in, so an empty list also
     * means "nowhere to move it to".
     */
    queueOptions: { id: number; name: string }[] = [];
    currentQueueLabel: string | null = null;
    canMoveQueue = false;
    moveQueueOpen = false;
    moveQueueSelection = new UntypedFormGroup({
        configId: new UntypedFormControl('', Validators.required)
    });
    selectedQueueConfigId: number | null = null;

    /**
     * Edit or Remove Recipient (trade-platform#10428). An advisor who marks an investor "should not
     * sign" can still have them put back on the envelope by a form whose Custom signer field
     * resolves to their address — order 40776 — and until this tool the only remedy was Make
     * Changes: void the envelope, rebuild every form, re-collect every other signature.
     *
     * The list comes off the order rather than from an API call, so unlike Move Order Queue there is
     * no empty-list gate that keeps the card off a maintenance order — see
     * `supportsRecipientChanges`, which is what does.
     */
    recipientOptions: {
        id: string;
        name: string;
        role: string;
        email: string;
        status?: eSignEnvelopeRecipientStatusType;
    }[] = [];

    /**
     * Whether this surface has the tool AT ALL, as opposed to whether this order can use it right
     * now. Deliberately separate from `canChangeRecipients`: that one always comes with a reason
     * the admin can read and act on, and "your order type is not supported yet" is not one — the
     * card simply should not be there, exactly as Move Order Queue is not.
     *
     * Maintenance is the one facade that cannot service this: admin-api has no route for it under
     * /maintenance, so MaintenanceOrdersStoreFacade implements the action as a no-op, and
     * maintenance.routes.ts renders this same component. Phrased as the unsupported case rather
     * than as "is buy" so a third order type added later gets the card, with its API call as the
     * authority, instead of being silently hidden from it.
     *
     * A getter, not a field: `store.type` is fixed at injection, so there is nothing to recompute.
     */
    get supportsRecipientChanges() {
        return this.store.type !== 'maintenance';
    }

    /**
     * A refusal that belongs to the ORDER and applies whichever change is selected. Set from the
     * order; the per-selection refusals are in the getter below.
     */
    recipientChangeOrderBlockedReason: string | null = null;

    /**
     * What the admin needs to be told about the current selection, or null when there is nothing to
     * say. Each refusal carries different advice and they need to be told which applies — a
     * terminal status has no way out, the envelope names Make Changes, a finished signer names
     * neither.
     *
     * NOT the inverse of `canChangeRecipients`. Every refusal here has a reason, but not every
     * reason is a refusal: with nothing selected on a sent order this returns guidance beside a
     * button that stays enabled, because half the card is still usable. Read it as "what to show",
     * not as "whether to disable".
     *
     * A getter rather than a field because the refusals depend on what is currently SELECTED, not
     * on the order: since trade-platform#10503 a sent envelope blocks a removal but not an email
     * correction, so the sublabel has to change as the admin switches the Change dropdown.
     */
    get recipientChangeBlockedReason(): string | null {
        if (this.recipientChangeOrderBlockedReason) {
            return this.recipientChangeOrderBlockedReason;
        }
        if (!this.hasEnvelopeId) {
            return null;
        }
        // Mirrors lib-orders/admin/updateEnvelopeRecipients.js, which lifted the envelope gate for
        // emailChanged only. A removal post-send strands the signature tabs DocuSign already holds
        // for that person, and DocuSign will not complete an envelope with unassigned tabs.
        if (this.selectedRecipientAction === 'removed') {
            return 'This order has already been sent to DocuSign. Use Make Changes to pull it back before removing a recipient.';
        }
        // Ahead of the no-selection guidance below, not after it. That guidance promises an address
        // can still be corrected, and a finished signer is the one case where it cannot -- while
        // canChangeRecipients reads this same status whatever the action is. Checked last, the card
        // disabled its own button while the sublabel still offered the correction.
        const status = this.selectedRecipient?.status;
        if (status === 'Completed' || status === 'Declined') {
            return `This recipient has already ${
                status === 'Completed' ? 'signed' : 'declined'
            }, so their email address can no longer be changed.`;
        }
        // Nothing is selected yet: name the half that is unavailable rather than claiming the whole
        // card is, which is what the admin would otherwise read before touching either dropdown.
        if (!this.selectedRecipientAction) {
            return 'This order has been sent to DocuSign. An address can still be corrected; removing a recipient needs Make Changes.';
        }
        return null;
    }

    /**
     * Only the API's own gates, and the submit button's half of them. Deliberately not the same
     * question as the sublabel above: the "an address can still be corrected" line is guidance on a
     * card that is perfectly usable, so it must not disable the button.
     */
    get canChangeRecipients(): boolean {
        if (this.recipientChangeOrderBlockedReason) {
            return false;
        }
        if (!this.hasEnvelopeId) {
            return true;
        }
        if (this.selectedRecipientAction === 'removed') {
            return false;
        }
        const status = this.selectedRecipient?.status;
        return status !== 'Completed' && status !== 'Declined';
    }
    recipientChangeOpen = false;
    recipientChangeSelection = new UntypedFormGroup({
        recipientId: new UntypedFormControl('', Validators.required),
        action: new UntypedFormControl('', Validators.required),
        // Validators.email rather than a bespoke pattern: this only has to catch a typo before a
        // round trip, and the server applies the authoritative Joi .email() check.
        newEmail: new UntypedFormControl('', Validators.email),
        reason: new UntypedFormControl('')
    });
    selectedRecipientId: string | null = null;
    selectedRecipientAction: OrderRecipientChangeAction | null = null;
    recipientActions: { id: OrderRecipientChangeAction; name: string }[] = [
        { id: 'removed', name: 'Remove recipient' },
        { id: 'emailChanged', name: 'Change email address' }
    ];

    integrationPartners: any[] = [
        { id: 'docupace', name: 'Docupace' },
        { id: 'gateway', name: 'Gateway' }
    ];
    resubmitIntegrationOpen = false;
    resubmitIntegrationSelection = new UntypedFormGroup({
        integrationPartnerId: new UntypedFormControl('', Validators.required)
    });
    selectedIntegrationPartner: string | null = null;

    buttonTypeSecondary = BUTTON_TYPE.secondary;
    buttonTypeLink = BUTTON_TYPE.link;

    /** Inserted by Angular inject() migration for backwards compatibility */
    constructor(...args: unknown[]);

    constructor() {}

    ngOnInit() {
        this.subscriptions.push(
            (this.route.parent as ActivatedRoute).params.subscribe(params => {
                if (params['orderId']) {
                    this.orderId = params['orderId'];
                }
                const profile = this.profileStoreFacade.profile;
                // The `admin` child route carries no router guard, so this is the only thing
                // protecting a direct URL. It used to check the role alone and no feature at all,
                // which let a role-holder in while the tab that leads here was hidden; the shared
                // predicate closes that and admits client order admins (roadmap#333).
                //
                // canAccessAdmin gates the template as well, because navigate() resolves a tick
                // later and the panel would otherwise paint before the redirect lands.
                this.canAccessAdmin = canAccessOrderAdmin(profile);
                if (!this.canAccessAdmin) {
                    if (this.orderId) {
                        this.router.navigate(this.store.routes.process.overview(this.orderId));
                    } else {
                        this.router.navigate(this.store.routes.status());
                    }
                }
            }),
            this.store.orderSuccess$.subscribe(order => {
                if (this.orderId && order.id !== this.orderId) {
                    this.loadOrder();
                }
                if (this.canReadGatewayFiles) {
                    this.store.actions.orderLoadIfgGatewayFiles.dispatch({ orderId: this.orderId });
                    // Same gate, same reason: queueOptions requires the orderAdmin feature, and
                    // UnauthorizedInterceptor ends the session on any 403 before the failure handler
                    // runs. The card is AIX-only, so nobody else needs the list either.
                    this.store.actions.orderLoadQueueOptions.dispatch({ orderId: this.orderId });
                }

                this.status = order.status;
                this.isWetsign = order.wetSign === true;
                this.canConvertToWetsign =
                    getOrderStatusIndex(this.status) >=
                    getOrderStatusIndex(orderStatus.pendingApprovalProcess);
                // Mirrors both API gates in lib-orders/admin/replaceForm.js: step 5 onward with no
                // status exclusions, AND a terminal envelope. Kept as its own flag rather than
                // reusing canConvertToWetsign so the two can diverge if either gate changes.
                //
                // The envelope half agrees with isEnvelopeTerminal, but is phrased the other way round:
                // it names the statuses that are still LIVE, and treats everything else as finished.
                //
                // The API enumerates the terminal statuses and refuses anything it does not recognise,
                // which is right for the authoritative gate. Here it would be wrong. Naming the
                // terminal set meant Voided - which the API accepts, because a voided envelope returns
                // no documents - disabled this card permanently, on an order no later event would ever
                // clear. Listing the live statuses instead means an unfamiliar status leaves the card
                // open: the admin may waste an upload and gets a 409 that explains itself and re-gates
                // the card, which is a far better failure than a dead end.
                //
                // An envelopeId with no recorded status is still treated as live. That is absence
                // rather than an unfamiliar status, the API refuses it, and it resolves as soon as a
                // webhook arrives.
                //
                // A wet-sign order is never treated as live, whatever its envelope says. Converting to
                // wet-sign leaves the envelope behind with its status frozen — usually at Sent — and
                // nothing ever advances it again, so judging by it disabled every wet-sign order that
                // had ever been sent for signature, with no way for an admin to clear it. Such an order
                // is not being signed in DocuSign at all.
                const liveEnvelopeStatuses: string[] = [
                    envelopeStatus.created,
                    envelopeStatus.sent,
                    envelopeStatus.delivered
                ];
                const envelopeState = order.eSign?.envelope?.status;
                this.replaceFormEnvelopeOpen =
                    !!order.envelopeId &&
                    order.wetSign !== true &&
                    (!envelopeState || liveEnvelopeStatuses.includes(envelopeState));
                this.canReplaceForm =
                    getOrderStatusIndex(this.status) >=
                        getOrderStatusIndex(orderStatus.pendingApprovalProcess) &&
                    !this.replaceFormEnvelopeOpen;
                this.canMoveBackInWorkflowStatus = [
                    orderStatus.pendingFirmApproval,
                    orderStatus.pendingCustodianApproval,
                    orderStatus.pendingFundSponsorApproval,
                    orderStatus.pendingSubmissionToCustodian,
                    orderStatus.pendingSubmissionToFundSponsor,
                    orderStatus.pendingSubmissionToTransferAgent,
                    orderStatus.pendingManualSubmission
                ].includes(this.status);
                if (order.approvals && order.approvals.length > 1) {
                    const currentApproval = order.approvals[0];
                    const previousApproval = order.approvals[1];
                    if (
                        (currentApproval.config.firmId &&
                            currentApproval.config.firmId === previousApproval.config.firmId) ||
                        (currentApproval.config.custodianId &&
                            currentApproval.config.custodianId ===
                                previousApproval.config.custodianId) ||
                        (currentApproval.config.fundSponsorId &&
                            currentApproval.config.fundSponsorId ===
                                previousApproval.config.fundSponsorId)
                    ) {
                        this.hasPreviousApproval = true;
                    }
                }
                this.canMoveForwardInWorkflow = [
                    orderStatus.pendingFirmApproval,
                    orderStatus.pendingCustodianApproval,
                    orderStatus.pendingFundSponsorApproval
                ].includes(this.status);
                this.hasValidAddBlindCarbonCopyToEnvelopeState = [
                    orderStatus.pendingSignatures,
                    orderStatus.pendingApprovalProcess,
                    orderStatus.pendingFirmApproval,
                    orderStatus.pendingSubmissionToCustodian,
                    orderStatus.pendingCustodianApproval,
                    orderStatus.pendingSubmissionToFundSponsor,
                    orderStatus.pendingFundSponsorApproval,
                    orderStatus.offline,
                    orderStatus.pendingSubmissionToTransferAgent,
                    orderStatus.pendingManualSubmission,
                    orderStatus.submitted,
                    orderStatus.cancelled,
                    orderStatus.nigo,
                    orderStatus.needsClarification
                ].includes(this.status);
                this.hasEnvelopeId = !!order.envelopeId;
                this.canPushProductProfileData = [
                    orderStatus.draft,
                    orderStatus.formsComplete,
                    orderStatus.readyForPresignatureReview,
                    orderStatus.pendingPresignatureReview,
                    orderStatus.presignatureReviewComplete,
                    orderStatus.readyToSend,
                    orderStatus.pendingSignatures,
                    orderStatus.nigo,
                    orderStatus.needsClarification
                ].includes(this.status);
                // Matches the one status lib-orders/admin/moveQueue.js accepts. A queue is a
                // property of the firm-approval step, so this is an equality rather than a
                // threshold: an order that has moved past firm approval has no queue to change.
                this.canMoveQueue = this.status === orderStatus.pendingFirmApproval;

                // Edit or Remove Recipient (trade-platform#10428).
                //
                // Only signers are offered. envelope.recipients also carries the receive-only rows
                // a mappable order always has - the advisor is added for the mapping UI whether or
                // not they sign - and removing one changes nothing about who signs. A recipient
                // with no `role` is skipped too: role is what the API addresses the change by, and
                // an envelope persisted before the field existed has none.
                //
                // mergedRoles are offered as entries in their own right. A Custom signer sharing
                // the advisor's address gets no recipient of its own, and "remove the CoSigner"
                // has to remain expressible without removing the advisor - the API strips just the
                // merged role in that case.
                const envelopeRecipients = order.eSign?.envelope?.recipients ?? [];
                this.recipientOptions = envelopeRecipients
                    .filter(recipient => recipient.hasSignatureFields !== false && !!recipient.role)
                    .flatMap(recipient => {
                        const own = {
                            id: `${recipient.role} ${recipient.email}`,
                            name: `${recipient.role} — ${recipient.fullName} (${recipient.email})`,
                            role: recipient.role as string,
                            email: recipient.email,
                            // Carried so the card can refuse a correction on somebody who has
                            // already signed (trade-platform#10503) rather than spending a round
                            // trip on a 409. A merged role shares its host's status by definition:
                            // it is the same person, on the same envelope.
                            status: recipient.status
                        };
                        const merged = (recipient.mergedRoles ?? []).map(mergedRole => ({
                            id: `${mergedRole} ${recipient.email}`,
                            name: `${mergedRole} — ${recipient.fullName} (${recipient.email}, signs with ${recipient.role})`,
                            role: mergedRole,
                            email: recipient.email,
                            status: recipient.status
                        }));
                        return [own, ...merged];
                    });

                // The order-level gate, matching the TERMINAL_STATUSES list in
                // lib-orders/admin/updateEnvelopeRecipients.js. The envelope gate is NOT here: it
                // depends on which change is selected, so it lives in recipientChangeBlockedReason.
                //
                // Note this is the only recipient refusal the order itself can settle. The order
                // type is supportsRecipientChanges, which gates whether the card renders at all
                // rather than whether this order can use it: every reason recorded here is one the
                // admin is shown and can act on, and the card not existing on this surface is not.
                const recipientTerminalStatuses: string[] = [
                    orderStatus.completed,
                    orderStatus.cancelled,
                    orderStatus.archived
                ];
                this.recipientChangeOrderBlockedReason = recipientTerminalStatuses.includes(
                    this.status
                )
                    ? `Recipients cannot be changed on an order that is ${this.status}.`
                    : null;

                this.isIfgAdvisorOrder = order.firm.crdnNumber === '7717';
                const integrationApproval = order.approvals?.find(
                    a => a.config.role === 'integrationUser'
                );
                this.integrationApprovalConfig = integrationApproval?.config ?? null;
                this.canResubmitFileToIfgGateway = [
                    orderStatus.pendingFirmApproval,
                    orderStatus.pendingSubmissionToCustodian,
                    orderStatus.pendingCustodianApproval,
                    orderStatus.pendingSubmissionToFundSponsor,
                    orderStatus.pendingFundSponsorApproval,
                    orderStatus.offline,
                    orderStatus.pendingSubmissionToTransferAgent,
                    orderStatus.pendingManualSubmission,
                    orderStatus.submitted,
                    orderStatus.cancelled,
                    orderStatus.nigo,
                    orderStatus.needsClarification,
                    orderStatus.completed,
                    orderStatus.archived
                ].includes(this.status);

                /**
                 * NOT cancelled, archived, complete
                 */

                this.orderForms = Object.entries(order.forms).map(([key, value]) => ({
                    name: `${value.documentOwner} - ${value.name}`,
                    id: key
                }));
            }),
            this.store.actions.orderLoadIfgGatewayFiles.success$.subscribe(fileList => {
                this.ifgGatewayFiles = fileList.payload;
            }),
            this.store.actions.orderReplaceForm.success$.subscribe(() => {
                this.replaceFormInFlight = false;
                this.replaceFormSelection.enable();
                this.replaceFormOpen = false;
                this.replaceFormId = null;
                this.replaceFormFile = null;
                this.replaceFormSelection.reset({ formId: '' });
                this.onActionSuccess('Form PDF replaced. The order is now wet-sign.');
            }),
            this.store.actions.orderReplaceForm.failure$.subscribe(action => {
                this.onReplaceFormFailure(action.payload?.error);
            }),
            this.store.actions.orderPushUpdatedForm.success$.subscribe(() => {
                this.updateFormOpen = false;
                this.onActionSuccess('Form has been updated.');
            }),
            this.store.actions.orderPushUpdatedForm.failure$.subscribe(() => {
                this.onActionFailure('Error updating form.');
            }),
            this.store.actions.orderToWetsign.success$.subscribe(() => {
                this.onActionSuccess('Order has been converted to wetsign.');
            }),
            this.store.actions.orderToWetsign.failure$.subscribe(() => {
                this.onActionFailure('Error converting order to wetsign.');
            }),
            this.store.actions.orderMarkComplete.success$.subscribe(() => {
                this.onActionSuccess('Order has been marked complete.');
            }),
            this.store.actions.orderMarkComplete.failure$.subscribe(() => {
                this.onActionFailure('Error marking order complete.');
            }),
            this.store.actions.orderNigoToNeedsClarification.success$.subscribe(() => {
                this.onActionSuccess('Order has been converted to Needs Clarification.');
            }),
            this.store.actions.orderNigoToNeedsClarification.failure$.subscribe(() => {
                this.onActionFailure('Error converting order to Needs Clarification.');
            }),
            this.store.actions.orderMoveBackInWorkflow.success$.subscribe(() => {
                this.onActionSuccess('Order has been moved back in workflow.');
            }),
            this.store.actions.orderMoveBackInWorkflow.failure$.subscribe(() => {
                this.onActionFailure('Error moving order back in workflow.');
            }),
            this.store.actions.orderMoveForwardInWorkflow.success$.subscribe(() => {
                this.onActionSuccess('Order has been moved forward in workflow.');
            }),
            this.store.actions.orderMoveForwardInWorkflow.failure$.subscribe(() => {
                this.onActionFailure('Error moving order forward in workflow.');
            }),
            this.store.actions.orderResubmit.success$.subscribe(() => {
                this.onActionSuccess('Order has been resubmitted.');
            }),
            this.store.actions.orderResubmit.failure$.subscribe(() => {
                this.onActionFailure('Error resubmitting order.');
            }),
            this.store.actions.orderAddBlindCarbonCopyToEnvelope.success$.subscribe(() => {
                this.onActionSuccess('BCC email address has been added to envelope.');
            }),
            this.store.actions.orderAddBlindCarbonCopyToEnvelope.failure$.subscribe(() => {
                this.onActionFailure('Error adding BCC email address to envelope.');
            }),
            this.store.actions.orderUpdateProductProfileData.success$.subscribe(() => {
                this.onActionSuccess('Updated product profile has been pushed to order.');
            }),
            this.store.actions.orderUpdateProductProfileData.failure$.subscribe(() => {
                this.onActionFailure('Error pushing updated product profile to order.');
            }),
            this.store.actions.orderResubmitFileToIfgGateway.success$.subscribe(result => {
                this.resubmitFileOpen = false;
                const { isSuccess } = result.payload[0];
                if (!isSuccess) {
                    this.onActionFailure(
                        `Resubmission attempt was understood, but failed during IFG integration process`
                    );
                } else {
                    this.onActionSuccess(`File has been successfully resubmitted.`);
                    this.store.actions.orderLoadIfgGatewayFiles.dispatch({ orderId: this.orderId });
                }
            }),
            this.store.actions.orderResubmitFileToIfgGateway.failure$.subscribe(() => {
                this.onActionFailure('Error resubmitting file.');
            }),
            this.store.actions.orderRegenerateFileList.success$.subscribe(result => {
                this.onActionSuccess('File List regenerated.');
            }),
            this.store.actions.orderRegenerateFileList.failure$.subscribe(() => {
                this.onActionFailure('Error resubmitting file.');
            }),
            this.store.actions.orderResubmitOrderToIfgGateway.success$.subscribe(() => {
                this.resubmitIntegrationOpen = false;
                this.onActionSuccess('Order has been resubmitted to the integration partner.');
            }),
            this.store.actions.orderResubmitOrderToIfgGateway.failure$.subscribe(() => {
                this.onActionFailure('Error resubmitting order to integration partner.');
            }),
            this.store.actions.orderLoadQueueOptions.success$.subscribe(action => {
                // The facade types success$ as a bare AixAction, so the payload arrives untyped;
                // named here so the decoder's shape is what the rest of this block is written
                // against.
                const { currentLabel, options } = action.payload as OrderQueueOptions;
                this.currentQueueLabel = currentLabel;
                // The queue the order is already in is filtered out rather than shown and disabled:
                // the API refuses a move to it, so offering it would only produce a 422. That also
                // makes `queueOptions.length` mean "somewhere to move to", which is what the
                // template gates the card on.
                this.queueOptions = options
                    .filter(option => !option.isCurrent)
                    .map(option => ({ id: option.configId, name: option.label }));
                this.clearSelectedQueueIfUnlisted();
                this.ref.detectChanges();
            }),
            // Deliberately silent. This fires on load, not on anything the admin did, and the only
            // consequence of a failure is that the card does not render - there is nothing for them
            // to act on, and a red banner over the whole panel would misattribute the failure to
            // whichever action they had just run.
            this.store.actions.orderLoadQueueOptions.failure$.subscribe(() => {
                this.queueOptions = [];
                this.currentQueueLabel = null;
                this.clearSelectedQueueIfUnlisted();
                this.ref.detectChanges();
            }),
            this.store.actions.orderMoveQueue.success$.subscribe(() => {
                this.moveQueueOpen = false;
                this.selectedQueueConfigId = null;
                this.moveQueueSelection.reset({ configId: '' });
                // onActionSuccess reloads the order, and the orderSuccess$ subscription above
                // re-dispatches the option load off the back of it - which is needed, not just
                // tidy: the queue just moved to is now the current one and has to drop out of the
                // list.
                this.onActionSuccess('Order has been moved to the selected queue.');
            }),
            this.store.actions.orderMoveQueue.failure$.subscribe(action => {
                this.onMoveQueueFailure(action.payload?.error);
            }),
            this.store.actions.orderUpdateEnvelopeRecipients.success$.subscribe(() => {
                const applied = this.selectedRecipientAction;
                this.resetRecipientChange();
                // onActionSuccess reloads the order, which rebuilds recipientOptions off the new
                // envelope - needed, not just tidy: a removed recipient has to leave the list, and
                // a corrected address has to be what the next change is addressed by.
                this.onActionSuccess(
                    applied === 'removed'
                        ? 'Recipient removed. The change is recorded in the order history.'
                        : 'Recipient email updated. The change is recorded in the order history.'
                );
            }),
            this.store.actions.orderUpdateEnvelopeRecipients.failure$.subscribe(action => {
                this.onUpdateEnvelopeRecipientsFailure(action.payload?.error);
            })
        );
    }

    /**
     * A recipient change is refused for several quite different reasons, and catchHttpError drops
     * the server's explanation before the store sees it - the status code is all that survives,
     * exactly as in onReplaceFormFailure and onMoveQueueFailure.
     *
     * 400 covers the most cases and is the least specific as a result: a role that no longer
     * matches anybody, a change that would leave the order with no signers at all, and one that
     * would make the order un-e-signable all arrive the same way. The wording names the two an
     * admin can act on rather than guessing between them.
     *
     * The reload matters as much as the wording: the card's gates come from the order the page
     * loaded with, so an order that was sent underneath the admin would otherwise keep offering the
     * same change and failing the same way.
     */
    onUpdateEnvelopeRecipientsFailure(error?: { options?: { status?: number } }) {
        const status = error?.options?.status;
        let message: string;
        switch (status) {
            case 409:
                // Since trade-platform#10503 this covers more than the envelope: a removal on a
                // sent order, a recipient who has signed or declined in the window since the page
                // loaded, and one the order cannot match to the DocuSign envelope at all. The
                // reload below is what resolves it in every case, so lead with that rather than
                // guessing which of the three happened.
                message =
                    'This recipient can no longer be changed. They may have signed, or the order may have been sent or reached a final status. Reload the order to see where it stands; use Make Changes to pull it back.';
                break;
            case 410:
                // DocuSign's own refusal, mapped by sign-service: the envelope is voided, completed
                // or otherwise locked. Distinct from the 409s above because nothing about the order
                // is wrong - the envelope has moved on.
                message =
                    'DocuSign will no longer accept changes to this envelope. It may have been completed or voided. Reload the order to see its current state.';
                break;
            case 400:
                message =
                    'This change cannot be applied. It would either leave the order with no signers, or the recipient is no longer on the envelope - reload and check the current list.';
                break;
            case 404:
                message = 'That order could not be found. Reload and try again.';
                break;
            default:
                message = 'Failed to change the recipient.';
        }

        this.onActionFailure(message);
        this.loadOrder();
    }

    private resetRecipientChange() {
        this.recipientChangeOpen = false;
        this.selectedRecipientId = null;
        this.selectedRecipientAction = null;
        this.recipientChangeSelection.reset({
            recipientId: '',
            action: '',
            newEmail: '',
            reason: ''
        });
    }

    /**
     * A move is refused for one of three reasons, and catchHttpError drops the server's explanation
     * before the store sees it - the status code is all that survives, exactly as in
     * onReplaceFormFailure.
     *
     * The reload matters as much as the wording: the card's gates come from the order the page
     * loaded with, so an order that moved underneath the admin would otherwise keep offering the
     * same move and failing the same way. It also re-dispatches the option load, via the
     * orderSuccess$ subscription, so the dropdown re-gates along with the button.
     */
    onMoveQueueFailure(error?: { options?: { status?: number } }) {
        const status = error?.options?.status;
        let message: string;
        switch (status) {
            case 422:
                message =
                    'This order can no longer be moved to that queue. It may have left Pending Firm Approval, or already be in the queue you picked.';
                break;
            case 404:
                message = 'That order could not be found. Reload and try again.';
                break;
            default:
                message = 'Failed to move the order to another queue.';
        }

        this.onActionFailure(message);
        this.loadOrder();
    }

    onActionSuccess(message: string) {
        this.notificationMessage = message;
        this.notificationType = 'ok';
        this.notificationRef()?.openNotification();
        this.ref.detectChanges();

        this.loadOrder();
    }

    onActionFailure(message: string) {
        this.notificationMessage = message;
        this.notificationType = 'error';
        this.notificationRef()?.openNotification();
        this.ref.detectChanges();
    }

    loadOrder() {
        this.store.actions.getOrder.dispatch({
            orderId: this.orderId,
            reducerSuffix: this.reducerSuffix
        });
    }

    /** The dropdown's display name for the selected form; the raw id is a UUID. */
    get selectedFormName(): string {
        return this.orderForms.find(form => form.id === this.replaceFormId)?.name ?? '';
    }

    /** The queue's name for the confirmation modal; the raw id is an approval config id. */
    get selectedQueueName(): string {
        return this.queueOptions.find(queue => queue.id === this.selectedQueueConfigId)?.name ?? '';
    }

    /** The whole chosen recipient: the change is addressed by its role AND its current address. */
    get selectedRecipient() {
        return this.recipientOptions.find(option => option.id === this.selectedRecipientId) ?? null;
    }

    /** The trimmed new address, or null when the field is empty or this is not an email change. */
    get newRecipientEmail(): string | null {
        if (this.selectedRecipientAction !== 'emailChanged') {
            return null;
        }
        const value = (this.recipientChangeSelection.get('newEmail')?.value ?? '').trim();
        return value.length ? value : null;
    }

    /**
     * Both dropdowns answered, the card not blocked, and - for an email change - a new address that
     * at least parses. The server re-checks all of it; this only keeps the admin from spending a
     * round trip on a request that cannot succeed.
     */
    get canSubmitRecipientChange(): boolean {
        if (!this.canChangeRecipients || !this.selectedRecipient || !this.selectedRecipientAction) {
            return false;
        }
        if (this.selectedRecipientAction === 'emailChanged') {
            return (
                !!this.newRecipientEmail && !!this.recipientChangeSelection.get('newEmail')?.valid
            );
        }
        return true;
    }

    /**
     * What to tell an admin whose replacement is blocked by the envelope. Shared by the card's
     * sublabel and the 409 notification so the advice cannot drift: one is read before attempting an
     * upload, the other after a wasted one, and they describe the same situation.
     *
     * For an AIX order admin, converting to wet-sign is the way out, not just waiting. The API treats
     * a wet-sign order as terminal whatever its envelope says, and Convert Order to Wetsign is a card
     * further down the panel - so an admin told only to wait was being sent away from an action
     * already in front of them.
     *
     * Keyed on canConvertToWetsign because that card shares this one's status floor. Below
     * pendingApprovalProcess both gates are closed and the envelope sublabel suppresses the status
     * one, so naming the conversion there would point at the disabled button directly below.
     *
     * Keyed on isClientOrderAdminOnly for the same reason in the other direction: the conversion is
     * AIX-only (product pulled it back from the delegated set - roadmap#333, comment 5542931994), so
     * for a client tenant's administrator it is not an escape available at any status, and naming it
     * would send them to a card that is not rendered for them at all.
     */
    get envelopeOpenAdvice(): string {
        if (this.isClientOrderAdminOnly) {
            return 'This order is out for signature. A form cannot be replaced until the envelope has been completed or declined.';
        }

        if (this.canConvertToWetsign) {
            return 'This order is out for signature. Convert it to wetsign using the action below to replace the form now, or wait until the envelope has been completed or declined.';
        }

        return 'This order is still collecting signatures. Neither replacing a form nor converting to wetsign is available until the envelope has been completed or declined.';
    }

    /**
     * A replacement can be refused for four quite different reasons, and each one tells the admin to do
     * something different. A single "Failed to replace the form PDF." told them none of it.
     *
     * The status code is all there is to work with: catchHttpError replaces the server's message body
     * with either a support blurb or its default text before the store ever sees it, so the 409's
     * explanation does not survive the trip. The code does.
     *
     * Reloading the order matters as much as the wording. The card's gates come from the order it
     * loaded with, so after a refusal caused by the order moving underneath it - most of all an
     * envelope opening - the page would otherwise keep offering the same replacement and failing the
     * same way indefinitely. The reload re-gates the card, which for an open envelope means the submit
     * disables itself and says why.
     */
    onReplaceFormFailure(error?: { options?: { status?: number } }) {
        this.replaceFormInFlight = false;
        this.replaceFormSelection.enable();

        const status = error?.options?.status;
        let message: string;
        switch (status) {
            case 409:
                // Read from the order the page loaded with, which the 409 may have just outdated.
                // The loadOrder() below re-gates the card, so its sublabel - not this notification -
                // is the authoritative statement once the reload lands.
                message = this.envelopeOpenAdvice;
                break;
            case 422:
                message =
                    'This order is no longer in a status that allows a form to be replaced. Reload and check the order status.';
                break;
            case 404:
                message =
                    'That form no longer has a document to replace. Reload the order and pick the form again.';
                break;
            case 400:
                message =
                    'The file was rejected. It must be a PDF, and within the upload size limit.';
                break;
            default:
                message = 'Failed to replace the form PDF.';
        }

        this.onActionFailure(message);

        // Re-gate the card against current state rather than the state the page was loaded with.
        this.loadOrder();
    }

    onReplaceFormFileSelected(event: Event) {
        const input = event.target as HTMLInputElement;
        this.replaceFormFile = input.files?.length ? input.files[0] : null;
        // Clear the input now that the File is captured. A file input fires no `change` event when
        // the same file is chosen again, and the submit button is gated on replaceFormFile — so
        // after a successful replace (which nulls replaceFormFile) re-choosing that same PDF would
        // be a silent no-op with the button stuck disabled. Keeping the input empty on every path
        // means the two can never disagree. The captured File stays valid once the input is
        // cleared, and the filename shown in the card comes from replaceFormFile, not the input.
        input.value = '';
    }

    confirmationModalSelected(option: string) {
        if (option === 'Yes, continue' && this.pendingAction) {
            switch (this.pendingAction) {
                case 'toWetsign':
                    this.store.actions.orderToWetsign.dispatch({
                        orderId: this.orderId
                    });
                    break;
                case 'toNeedsClarification':
                    this.store.actions.orderNigoToNeedsClarification.dispatch({
                        orderId: this.orderId
                    });
                    break;
                case 'markComplete':
                    this.store.actions.orderMarkComplete.dispatch({
                        orderId: this.orderId
                    });
                    break;
                case 'resubmit':
                    this.store.actions.orderResubmit.dispatch({
                        orderId: this.orderId
                    });
                    break;
                case 'updateForm':
                    if (this.selectedFormId) {
                        this.store.actions.orderPushUpdatedForm.dispatch({
                            orderId: this.orderId,
                            formId: this.selectedFormId
                        });
                    }
                    break;
                case 'replaceForm':
                    // Both are required: without a form there is no slot to replace, and without
                    // a file there is nothing to put in it. The in-flight guard is belt-and-braces
                    // alongside the disabled button — the modal can still be confirmed by keyboard.
                    if (this.replaceFormId && this.replaceFormFile && !this.replaceFormInFlight) {
                        this.replaceFormInFlight = true;
                        // aix-dropdown has no disabled input; disabling its control is what
                        // actually stops the form being changed mid-upload.
                        this.replaceFormSelection.disable();
                        this.store.actions.orderReplaceForm.dispatch({
                            orderId: this.orderId,
                            formId: this.replaceFormId,
                            file: this.replaceFormFile
                        });
                    }
                    break;
                case 'moveQueue':
                    // Re-checked rather than trusted from the disabled button: the modal can still
                    // be confirmed by keyboard, and the status may have moved since it opened.
                    if (this.selectedQueueConfigId && this.canMoveQueue) {
                        this.store.actions.orderMoveQueue.dispatch({
                            orderId: this.orderId,
                            configId: this.selectedQueueConfigId
                        });
                    }
                    break;
                case 'updateEnvelopeRecipients': {
                    // Re-checked rather than trusted from the disabled button: the modal can still
                    // be confirmed by keyboard, and the order may have been sent since it opened.
                    const recipient = this.selectedRecipient;
                    if (
                        recipient &&
                        this.selectedRecipientAction &&
                        this.canSubmitRecipientChange
                    ) {
                        const reason = (
                            this.recipientChangeSelection.get('reason')?.value ?? ''
                        ).trim();
                        this.store.actions.orderUpdateEnvelopeRecipients.dispatch({
                            orderId: this.orderId,
                            change: {
                                role: recipient.role,
                                // Always sent. The server resolves the change against the recipient
                                // holding this address, which is what keeps a removal off a
                                // namesake role and what makes the override expire if the role is
                                // later re-pointed at somebody else.
                                originalEmail: recipient.email,
                                action: this.selectedRecipientAction,
                                newEmail: this.newRecipientEmail ?? undefined,
                                reason: reason.length ? reason : undefined
                            }
                        });
                    }
                    break;
                }
                case 'moveBackInWorkflow':
                    this.store.actions.orderMoveBackInWorkflow.dispatch({
                        orderId: this.orderId
                    });
                    break;
                case 'moveForwardInWorkflow':
                    this.store.actions.orderMoveForwardInWorkflow.dispatch({
                        orderId: this.orderId
                    });
                    break;
                case 'addBlindCarbonCopyToEnvelope':
                    this.store.actions.orderAddBlindCarbonCopyToEnvelope.dispatch({
                        orderId: this.orderId
                    });
                    break;
                case 'updateProductProfileData':
                    this.store.actions.orderUpdateProductProfileData.dispatch({
                        orderId: this.orderId
                    });
                    break;
                case 'resubmitFileToIfgGateway':
                    if (this.selectedFileTransmissionId) {
                        this.store.actions.orderResubmitFileToIfgGateway.dispatch({
                            orderId: this.orderId,
                            fileTransmissionId: this.selectedFileTransmissionId
                        });
                    }
                    break;
                case 'regenerateFileList':
                    this.store.actions.orderRegenerateFileList.dispatch({ orderId: this.orderId });
                    break;
                case 'resubmitOrderToIfgGateway':
                    if (this.selectedIntegrationPartner) {
                        this.store.actions.orderResubmitOrderToIfgGateway.dispatch({
                            orderId: this.orderId,
                            integrationType: this.selectedIntegrationPartner,
                            approvalConfig:
                                this.selectedIntegrationPartner === 'gateway'
                                    ? this.integrationApprovalConfig ?? undefined
                                    : undefined
                        });
                    }
                    break;
                default:
                    break;
            }
        } else {
            this.pendingAction = null;
        }
    }

    onClickUpdateOrder(action: string) {
        // aix-loading-button's isDisabled only toggles a class this codebase never styles, so it
        // neither dims the button nor blocks the click. Refuse here instead, or a click mid-upload
        // reopens the confirmation over an order that is already changing.
        if (this.replaceFormInFlight) {
            return;
        }

        this.pendingAction = action;

        this.notificationRef()?.closeNotification();
        this.notificationMessage = '';
        this.notificationType = 'ok';

        switch (action) {
            case 'toWetsign':
                this.modalText = {
                    title: 'Convert to Wetsign?',
                    text: 'Are you sure you want to convert this order to wetsign?'
                };
                break;
            case 'toNeedsClarification':
                this.modalText = {
                    title: 'Convert to Needs Clarification',
                    text: 'Are you sure you want to convert this order to Needs Clarification?'
                };
                break;
            case 'markComplete':
                this.modalText = {
                    title: 'Mark Complete',
                    text: 'Are you sure you want to mark this order as complete?'
                };
                break;
            case 'resubmit':
                this.modalText = {
                    title: 'Resubmit',
                    text: 'Are you sure you want to resubmit this order?'
                };
                break;
            case 'updateForm':
                this.modalText = {
                    title: 'Update Form',
                    text: `Are you sure you want to push the latest version of form id ${this.selectedFormId}?`
                };
                break;
            case 'replaceForm':
                this.modalText = {
                    title: 'Replace Form PDF',
                    // Names the form the way the dropdown does rather than showing its UUID, and
                    // splits the consequence out so the question is readable on its own.
                    text: `Replace ${this.selectedFormName} with ${this.replaceFormFile?.name}?`,
                    detail: 'The order will be set to wet-sign. The form stays system-owned, so it may be regenerated from current order data if the order is reworked.'
                };
                break;
            case 'moveQueue':
                this.modalText = {
                    title: 'Move Order Queue',
                    text: `Move this order to the ${this.selectedQueueName} queue?`,
                    detail: "The order stays in Pending Firm Approval. It leaves the current queue and the new queue's reviewers become responsible for signing it."
                };
                break;
            case 'updateEnvelopeRecipients': {
                const recipient = this.selectedRecipient;
                this.modalText =
                    this.selectedRecipientAction === 'removed'
                        ? {
                              title: 'Remove Recipient',
                              text: `Remove ${
                                  recipient?.name ?? 'this recipient'
                              } from this order?`,
                              detail: 'They will not be asked to sign, and their signature lines will be left blank on the rendered documents. The change is recorded in the order history and survives a Make Changes pull-back.'
                          }
                        : {
                              title: 'Change Recipient Email',
                              text: `Send ${recipient?.role ?? 'this recipient'}'s envelope to ${
                                  this.newRecipientEmail
                              } instead of ${recipient?.email ?? 'their current address'}?`,
                              // The consequence genuinely differs by state and the admin should
                              // know which they are getting. On a live envelope this reaches
                              // DocuSign now and re-sends, so the old address stops working the
                              // moment they confirm; pre-send it only changes what the envelope
                              // will be built with.
                              detail: this.hasEnvelopeId
                                  ? 'The envelope is already out, so DocuSign will be corrected now and the signing email re-sent to the new address. The change is recorded in the order history and survives a Make Changes pull-back.'
                                  : 'The new address is what DocuSign will be given. The change is recorded in the order history and survives a Make Changes pull-back.'
                          };
                break;
            }
            case 'moveBackInWorkflow':
                this.modalText = {
                    title: 'Move Back In Workflow',
                    text: 'Are you sure you want to move this order back in the workflow?'
                };
                break;
            case 'moveForwardInWorkflow':
                this.modalText = {
                    title: 'Move Forward In Workflow',
                    text: 'Are you sure you want to move this order forward in the workflow?'
                };
                break;
            case 'addBlindCarbonCopyToEnvelope':
                this.modalText = {
                    title: 'Add BCC Email Address to Envelope',
                    text: 'Are you sure you want to add the admin email to the envelope as a BCC address?'
                };
                break;
            case 'updateProductProfileData':
                this.modalText = {
                    title: 'Push Updated Product Profile Data to Order',
                    text: 'Are you sure you want to push updated product profile data to the order?'
                };
                break;
            case 'resubmitFileToIfgGateway':
                this.modalText = {
                    title: 'Resubmit File to IFG Gateway',
                    text: `Are you sure you want to resubmit file ${this.selectedFileTransmissionId} for IFG Gateway integration?`
                };
                break;
            case 'regenerateFileList':
                this.modalText = {
                    title: 'Regenerate File List',
                    text: `Are you sure you want to regenerate the file list?`
                };
                break;
            case 'resubmitOrderToIfgGateway':
                this.modalText = {
                    title: 'Resubmit Integration',
                    text: 'Are you sure you want to re-send this order to the integration partner?'
                };
                break;
            default:
                break;
        }
        this.confirmationModal()?.openModal();
    }

    toggleUpdateFormOpen() {
        this.updateFormOpen = !this.updateFormOpen;
    }

    toggleReplaceFormOpen() {
        this.replaceFormOpen = !this.replaceFormOpen;
    }

    toggleMoveQueueOpen() {
        this.moveQueueOpen = !this.moveQueueOpen;
    }

    toggleResubmitFileOpen() {
        this.resubmitFileOpen = !this.resubmitFileOpen;
    }

    toggleResubmitIntegrationOpen() {
        this.resubmitIntegrationOpen = !this.resubmitIntegrationOpen;
    }

    selectReplaceForm(e: any) {
        this.replaceFormId = e?.length === 1 ? e[0].id : null;
    }

    selectForm(e: any) {
        if (e?.length === 1) {
            this.selectedFormId = e[0].id;
        } else {
            this.selectedFormId = null;
        }
    }

    selectFile(e: any) {
        if (e?.length === 1) {
            this.selectedFileTransmissionId = e[0].id;
        } else {
            this.selectedFileTransmissionId = null;
        }
    }

    // aix-dropdown's ngModelChange emits an array even in single-select, same as every other
    // selector on this panel.
    selectQueue(e: any) {
        this.selectedQueueConfigId = e?.length === 1 ? e[0].id : null;
    }

    selectRecipient(e: any) {
        this.selectedRecipientId = e?.length === 1 ? e[0].id : null;
    }

    selectRecipientAction(e: any) {
        this.selectedRecipientAction = e?.length === 1 ? e[0].id : null;
        // A newEmail left behind from a switch to Remove and back would be submitted unreviewed,
        // and one left behind on a switch to Remove is simply ignored by the server - clearing it
        // keeps what the card shows and what would be sent in agreement either way.
        if (this.selectedRecipientAction !== 'emailChanged') {
            this.recipientChangeSelection.get('newEmail')?.reset('');
        }
    }

    toggleRecipientChangeOpen() {
        this.recipientChangeOpen = !this.recipientChangeOpen;
    }

    /**
     * Drops a selection the reloaded option list no longer offers.
     *
     * The list is re-read after every move, including a failed one: onMoveQueueFailure reloads the
     * order and the orderSuccess$ subscription re-dispatches the option load. A 422 of the "already
     * in the queue you picked" kind is exactly the case where the queue that was selected comes back
     * as the CURRENT one and is filtered out - and a selection that outlives its option leaves the
     * submit enabled against an id the list no longer holds, so selectedQueueName resolves to '' and
     * the confirmation reads "Move this order to the  queue?".
     */
    private clearSelectedQueueIfUnlisted() {
        if (
            this.selectedQueueConfigId === null ||
            this.queueOptions.some(queue => queue.id === this.selectedQueueConfigId)
        ) {
            return;
        }
        this.selectedQueueConfigId = null;
        this.moveQueueSelection.reset({ configId: '' });
    }

    selectIntegrationPartner(e: any) {
        if (e?.length === 1) {
            this.selectedIntegrationPartner = e[0].id;
        } else {
            this.selectedIntegrationPartner = null;
        }
    }

    ngOnDestroy() {
        this.subscriptions.forEach(s => s.unsubscribe());
    }
}
